import { AlertTriangle } from "lucide-react";

export type Turn = {
  speaker: "them" | "you";
  text: string;
  tone?: "neutral" | "toxic";
};

interface Props {
  turns: Turn[];
  mechanismLabel: string;
  mechanismDescription?: string;
  /** Optional heading override */
  eyebrow?: string;
}

/**
 * Structured "example analysis" UI block — mimics the real analyzer's chrome
 * without implying live detection. Fictional exchange only.
 */
const AnalysisExampleBlock = ({
  turns,
  mechanismLabel,
  mechanismDescription,
  eyebrow = "Example Analysis — fictional exchange",
}: Props) => {
  return (
    <div className="my-8 rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5 bg-white/[0.015]">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/80" />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-300/80">
            {eyebrow}
          </span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">
          Illustrative
        </span>
      </div>

      {/* Message thread */}
      <div className="px-4 md:px-6 py-6 space-y-3">
        {turns.map((t, i) => {
          const isThem = t.speaker === "them";
          const isToxic = t.tone === "toxic";
          return (
            <div
              key={i}
              className={`flex ${isThem ? "justify-start" : "justify-end"}`}
            >
              <div className="max-w-[85%]">
                <div
                  className={`text-[10px] font-mono uppercase tracking-[0.22em] mb-1 ${
                    isThem ? "text-white/40 text-left" : "text-white/40 text-right"
                  }`}
                >
                  {isThem ? "Them" : "You"}
                </div>
                <div
                  className={
                    isToxic
                      ? "rounded-2xl rounded-tl-sm px-4 py-3 bg-slate-900/60 border border-cyan-400/40 shadow-[0_0_24px_-8px_rgba(34,211,238,0.45)]"
                      : isThem
                      ? "rounded-2xl rounded-tl-sm px-4 py-3 bg-white/[0.06] border border-white/[0.08]"
                      : "rounded-2xl rounded-tr-sm px-4 py-3 bg-white/[0.04] border border-white/[0.08]"
                  }
                >
                  <p className="text-[15px] leading-relaxed text-white/90 font-serif italic">
                    "{t.text}"
                  </p>
                </div>

                {/* Callout under the last toxic bubble */}
                {isToxic &&
                  i === turns.map((x) => x.tone).lastIndexOf("toxic") && (
                    <div className="mt-3 rounded-lg border border-cyan-400/25 bg-gradient-to-br from-slate-900/80 to-cyan-950/30 px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <AlertTriangle className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-cyan-300">
                            {mechanismLabel}
                          </div>
                          {mechanismDescription && (
                            <p className="mt-1.5 text-[13.5px] leading-relaxed text-white/70">
                              {mechanismDescription}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnalysisExampleBlock;
